import maplibregl from "maplibre-gl"
import turfBooleanWithin from "@turf/boolean-within"
import {
  point as turfPoint,
  // featureCollection as turfFeatureCollection,
} from "@turf/helpers"

import Hook from "../utils/hook"

// const EMPTY_FEATURE_COLLECTION = turfFeatureCollection([])
const LM_SWE_TILE_URL = `https://new-maptiles.naturkartan-cdn.se/SWE/{z}/{x}/{y}.png`
const LM_NOR_TILE_URL = `https://new-maptiles.naturkartan-cdn.se/NOR/{z}/{x}/{y}.png`
const OUTDOOR_TILE_URL = `https://tile.thunderforest.com/outdoors/{z}/{x}/{y}.png?apikey=9106f29987fc41eda5246b5463ceeba2`

const LM_SWE_LAYER = {
  id: `lm-sweden-tileset-layer`,
  source: `lm-sweden-tileset-source`,
  type: `raster`,
  layout: {
    visibility: `none`,
  },
}

const LM_NOR_LAYER = {
  id: `lm-norway-tileset-layer`,
  source: `lm-norway-tileset-source`,
  type: `raster`,
  layout: {
    visibility: `none`,
  },
}

const OUTDOOR_LAYER = {
  id: `outdoor-tileset-layer`,
  source: `outdoor-tileset-source`,
  type: `raster`,
}

const LM_TILESET_BOUNDS_FEATURE_MIN_ZOOM = 6.5
const LM_TILESET_BOUNDS_FEATURE_MAX_ZOOM = 16.5
const LM_TILESET_BOUNDS_FEATURE = {
  type: `Feature`,
  properties: {},
  geometry: {
    type: `Polygon`,
    coordinates: [
      [
        [13.758064430533949, 69.63489360829101],
        [11.405819187595228, 67.6473765660352],
        [10.344563123488712, 65.1119566059588],
        [8.026108597097647, 64.19596589506867],
        [4.796192078472245, 63.00404526945087],
        [3.5541958853600875, 61.45435585341946],
        [4.006075563582414, 58.58136859572443],
        [7.06104986999452, 57.55702767286803],
        [10.23027887886974, 58.36347499015449],
        [11.38797354136787, 57.70050969373463],
        [12.385215016684981, 56.57668469239832],
        [12.31917444025072, 56.106673867470704],
        [12.545202579551272, 55.79547068750736],
        [12.881799612262483, 55.16513400827324],
        [15.275135363769294, 54.442180163202835],
        [19.74996043893006, 56.14377415027087],
        [20.490707807164426, 58.136522757466054],
        [20.314433125000164, 60.19234122063716],
        [19.670451047594696, 61.81109104105434],
        [20.569087125676702, 63.06703205632547],
        [22.189345546033138, 64.47939573058761],
        [24.659723741166715, 65.3821135510374],
        [25.1603354240917, 66.31954701166225],
        [25.0288794487129, 67.65336911439601],
        [24.577128969798167, 68.52890269652013],
        [21.55061585078232, 69.45649571458333],
        [19.305562300940522, 70.71900737886185],
        [13.758064430533949, 69.63489360829101],
      ],
    ],
  },
}

export default Hook(`Maplibre`, {
  targets: [`map`, `fullscreen`, `zoomIn`, `zoomOut`],
  values: { fullscreenClass: String },
  actions: [
    {
      target: `zoomIn`,
      name: `zoomIn`,
      type: `click`,
    },
    {
      target: `zoomOut`,
      name: `zoomOut`,
      type: `click`,
    },
    {
      target: `fullscreen`,
      name: `toggleFullscreen`,
      type: `click`,
    },
    {
      name: `fullscreenChange`,
      type: `fullscreenchange`,
    },
  ],
  map: null,
  currentTileset: `outdoor`, // lm|outdoor

  mounted() {
    this.loadHandlers = new Set()

    this.map = new maplibregl.Map({
      container: this.mapTarget,
      style: {
        version: 8,
        id: `naturkartan`,
        name: `Naturkartan`,
        pitch: 0,
        bearing: 0,
        sprite: `https://map.naturkartan.se/sprite/sprite`,
        glyphs: `https://map.naturkartan.se/glyphs/{fontstack}/{range}.pbf`,
        sources: {
          // base: {
          //   type: `geojson`,
          //   data: EMPTY_FEATURE_COLLECTION,
          // },
          "lm-sweden-tileset-source": {
            type: `raster`,
            tiles: [LM_SWE_TILE_URL],
            tileSize: 256,
            maxzoom: 19,
          },
          "lm-norway-tileset-source": {
            type: `raster`,
            tiles: [LM_NOR_TILE_URL],
            tileSize: 256,
            maxzoom: 19,
          },
          "outdoor-tileset-source": {
            type: `raster`,
            tiles: [OUTDOOR_TILE_URL],
            tileSize: 256,
            maxzoom: 19,
          },
        },
        layers: [
          LM_SWE_LAYER,
          LM_NOR_LAYER,
          OUTDOOR_LAYER,
          // {
          //   id: `base-tileset`,
          //   source: `base`,
          //   type: `symbol`,
          // },
        ],
      },
      center: [14, 63],
      zoom: 4,
      attributionControl: false,
    })

    this.map.on(`load`, () => this.loadHandlers.forEach((fn) => fn(this.map)))
    this.map.on(`move`, this.mapMove.bind(this))
  },

  destroyed() {
    this.loadHandlers.clear()
    this.map?.remove()
  },

  addLoadHandler(fn) {
    this.loadHandlers.add(fn)
  },

  removeLoadHandler(fn) {
    this.loadHandlers.delete(fn)
  },

  zoomInAction() {
    this.map.zoomIn()
  },

  zoomOutAction() {
    this.map.zoomOut()
  },

  toggleFullscreenAction() {
    if (!document.fullscreenElement) this.el.requestFullscreen()
    else document.exitFullscreen()

    this.map?.resize()
  },

  fullscreenChangeAction() {
    if (!this.fullscreenClassValue) return
    if (document.fullscreenElement === this.el)
      this.classAdd(this.fullscreenClassValue)
    else this.classRemove(this.fullscreenClassValue)
  },

  mapMove() {
    if (!this.map) return

    const isLmTilesetMatch =
      LM_TILESET_BOUNDS_FEATURE_MIN_ZOOM < this.map.getZoom() &&
      this.map.getZoom() < LM_TILESET_BOUNDS_FEATURE_MAX_ZOOM &&
      turfBooleanWithin(
        turfPoint(this.map.getCenter().toArray()),
        LM_TILESET_BOUNDS_FEATURE
      )

    if (isLmTilesetMatch && this.currentTileset != `lm`) {
      this.map.setLayoutProperty(
        `lm-sweden-tileset-layer`,
        `visibility`,
        `visible`
      )
      this.map.setLayoutProperty(
        `lm-norway-tileset-layer`,
        `visibility`,
        `visible`
      )
      this.map.setLayoutProperty(`outdoor-tileset-layer`, `visibility`, `none`)
      this.currentTileset = `lm`
    } else if (!isLmTilesetMatch && this.currentTileset != `outdoor`) {
      this.map.setLayoutProperty(
        `lm-sweden-tileset-layer`,
        `visibility`,
        `none`
      )
      this.map.setLayoutProperty(
        `lm-norway-tileset-layer`,
        `visibility`,
        `none`
      )
      this.map.setLayoutProperty(
        `outdoor-tileset-layer`,
        `visibility`,
        `visible`
      )
      this.currentTileset = `outdoor`
    }
  },
})
